import {ClasseProfit} from './../../mapa/common/models/ClasseProfit';
/* tslint:disable:indent */
import {Injectable, Optional} from '@angular/core';
import {Avis} from '../../../../models/avis';
import {Annonce} from '../../common/models/annonce';
import {FormsEnum} from '../../../../models/enums/forms.enum';
import {TypeAnn} from '../../common/models/type.ann';
import {LogService} from '../../../shared/services/log.service';
import {StepperBuilderService} from '../../../avisDePub/creer/services/stepper.builder.service';
import {TypeFacturation} from '../../../../models/eforms/dila/TypeFacturation';
import {TypeAdresseTEMPOST} from '../../../../models/eforms/dila/TypeAdresseTEMPOST';
import {RadioCheckBoxEnum} from '../../common/radio.checkbox.enums';
import {CriteresIndexationHelper} from '../../common/helpers/CriteresIndexationHelper';
import {TypeIndexation} from '../../common/models/type.indexation';
import {TypeMotsClef} from '../../common/models/TypeMotsClef';
import {TypeDept} from '../../common/models/type.dept';
import {TypeAvisEnum} from '../../../../models/enums/type-avis.enum';
import {Annonceur} from '../../common/models/annonceur.pwd';
import {EformConditionsV2Service} from './eform-conditions-v2.service';
import {AvisDataTransferService} from '../../../shared/services/avis-data-transfer.service';
import {EformDuplicableEntityService} from './eform-duplicable-entity.service';

/**
 * Décrit un type de groupe répétable dont l'identifiant technique peut être régénéré.
 * - type   : préfixe du scheme de l'identifiant (ex. 'CON')
 * - locate : localise le tableau d'éléments de ce type dans le noticeResult
 */
interface DuplicableIdDescriptor {
  type: string;
  locate: (noticeResult: any) => any[];
}

@Injectable({
  providedIn: 'root'
})
export class EformsExtractorService {

  private LOG: LogService = new LogService(EformsExtractorService);

  /**
   * Groupes répétables traités par la régénération d'identifiants.
   */
  private readonly DUPLICABLE_ID_DESCRIPTORS: DuplicableIdDescriptor[] = [
    {type: 'TEN', locate: noticeResult => noticeResult.lottender},
    {type: 'CON', locate: noticeResult => noticeResult.settledcontract},
    {type: 'TPA', locate: noticeResult => noticeResult.tenderingparty},
  ];

  constructor(
    private stepperBuilderService: StepperBuilderService,
    private eformConditionsV2Service: EformConditionsV2Service,
    private avisDataTransferService: AvisDataTransferService,
    @Optional() private eformDuplicableEntityService?: EformDuplicableEntityService
  ) {
  }

  public mapFormToAvis(form, commonExtractor, actualFormIdAnnEmetteur): Avis {
    this.LOG.debug('EFORMS_EXTRACTOR', form?.controls?.eforms?.value);


    const avis: Avis = new Avis();
    const annonce: Annonce = new Annonce();
    annonce.ann = new TypeAnn();
    annonce.ann.fnSimple = null;
    annonce.ann.divers = null;
    annonce.ann.mapa = null;
    annonce.ann.facturation = this.getFacturation(form);
    annonce.ann.indexation = this.getIndexation(form);
    annonce.ann.idAnnEmetteur = actualFormIdAnnEmetteur;


    if (form?.controls?.eforms?.value.priorinformationnotice !== undefined) {
      annonce.ann.priorInformationNotice = form?.controls?.eforms?.getRawValue().priorinformationnotice;
    }
    if (form?.controls?.eforms?.value.contractnotice !== undefined) {
      annonce.ann.contractNotice = form?.controls?.eforms?.getRawValue().contractnotice;
    }
    if (form?.controls?.eforms?.value.contractawardnotice !== undefined) {
      annonce.ann.contractAwardNotice = form?.controls?.eforms?.getRawValue().contractawardnotice;
    }
    if (form?.controls?.eforms?.value.businessregistrationinformationnotice !== undefined) {
      annonce.ann.businessRegistrationInformationNotice = form?.controls?.eforms?.getRawValue().businessregistrationinformationnotice;
    }

    annonce.annonceur = this.extractAnnonceur(form);

    // cette ligne sert à étudier le systeme de compatibilité des version
    //annonce.eformsNotice = this.eformsSubjectService.notice;
    // voir comment récupérer le control
    avis.jsonSaisieFormulaire = annonce;
    avis.avisId = this.stepperBuilderService.avisId;
    // rendre ce choix dynamique
    avis.formulaire.code = FormsEnum[this.stepperBuilderService.formulaire];
    this.setAvisCritere(this.stepperBuilderService.formulaire, avis);

    // Injecter les éléments duplicables nettoyés depuis flatFields
    // pour les champs interdits qui sont dans des accordéons fermés
    this.injectCleanedDuplicableElements(annonce);

    // ✅ FIX 2025-12-24: Régénérer les IDs TEN/CON/TPA à XXX-0001 si null
    // Dans le cas 0 lauréats passant à selec-w, ces éléments ont un ID null
    // et doivent recevoir XXX-0001 car c'est le premier élément de chaque type
    this.regenerateNullDuplicableIds(annonce);

    // ✅ FIX 2026-03-06: Régénérer les IDs techniques des ORG (company.partyidentification.id.val)
    // Quand une nouvelle org est créée via "Ajouter nouveau", le formulaire ne contient pas d'ID technique.
    // Sans cet ID, le backend ne sait pas quelle org sauvegarder et écrase l'org 1 (ORG-0001) par défaut.
    this.regenerateNullOrganizationIds(annonce);

    // ✅ FIX 2026-04-22: Tronquer procurementprojectlot à UN SEUL élément avant la sauvegarde.
    // Après duplication d'un avis ENVOYÉ, le JSON peut contenir tous les lots (injectés par
    // fillAvisWithDuplicableObjects côté backend lors de l'envoi BOAMP). Le formulaire Angular
    // ne gère qu'un seul lot à la fois (le lot affiché). Si on envoie plusieurs lots au backend,
    // le persist écrase LOT-0002 avec les données de LOT-0001 car le form n'a pas mis à jour
    // les lots non-affichés. Ce bug est surtout visible sur Firefox à cause du timing HTTP différent.
    this.truncateProcurementProjectLot(annonce);

    // ✅ FIX 2026-04-03: Corriger l'ID du lot dans l'annonce extraite (LOT-XXXX figé au démarrage)
    // Au démarrage, getDuplicableEntity(LOT, 1) retourne les données du dernier lot sauvegardé (ex: LOT-0006).
    // Cet ID s'inscrit dans procurementprojectlot[0].id et reste figé quelle que soit la navigation entre lots.
    // Résultat : chaque sauvegarde envoie id=LOT-0006, le backend route tous les saves sur LOT-0006
    // et les autres lots (LOT-0003, LOT-0004, etc.) ne sont jamais mis à jour.
    // Ce fix récupère le vrai ID depuis listDuplicableEntity[actuallyIndexLotOrPar] et le réinjecte.
    this.fixCurrentLotId(annonce);

    return avis;
  }

  /**
   * Régénère les identifiants techniques manquants des groupes répétables (CON/TEN/TPA).
   *
   * Règle unique pilotée par l'évaluation du critère d'interdiction : on restaure l'identifiant d'un élément
   * si et seulement si son critère `forbidden` n'est PAS satisfait au moment du save. Un groupe
   * interdit reste sans identifiant (élément absent côté backend) ; un groupe à identifiant null qui n'est pas interdit
   * verra son identifiant restauré.
   */
  private regenerateNullDuplicableIds(annonce: Annonce): void {
    const noticeResult = this.getNoticeResult(annonce);
    if (!noticeResult) {
      return;
    }

    this.DUPLICABLE_ID_DESCRIPTORS.forEach(descriptor =>
      this.regenerateIdsForRepeatableGroup(noticeResult, descriptor));
  }

  /**
   * Régénère les identifiants manquants de tous les éléments d'un type de groupe répétable donné.
   */
  private regenerateIdsForRepeatableGroup(noticeResult: any, duplicableGroupIdDescriptor: DuplicableIdDescriptor): void {
    const groupElements = duplicableGroupIdDescriptor.locate(noticeResult);
    if (!Array.isArray(groupElements) || groupElements.length === 0) {
      return;
    }

    const displayedRealIndex = this.eformConditionsV2Service.getDisplayedRealIndex(duplicableGroupIdDescriptor.type);

    groupElements.forEach((groupElement, loopIndex) => {
      const groupIndex = this.resolveGroupIndex(groupElements, loopIndex, displayedRealIndex);
      this.restoreGroupIdIfAllowed(groupElement, duplicableGroupIdDescriptor.type, groupIndex);
    });
  }

  /**
   * Restaure l'identifiant d'un élément répétable uniquement s'il est manquant ET non interdit.
   */
  private restoreGroupIdIfAllowed(groupElement: any, groupType: string, groupIndex: number): void {
    if (!this.isGroupIdMissing(groupElement)) {
      return;
    }
    if (this.isRepeatableGroupForbidden(groupType, groupIndex)) {
      return;
    }
    this.assignRepeatableGroupId(groupElement, groupType, groupIndex);
  }

  /**
   * Index associé au groupe répétable :
   * Si on a un seul élément et displayedRealIndex est défini,
   * c'est l'élément affiché dans l'accordéon → utiliser displayedRealIndex
   * Sinon, utiliser l'index de la boucle
   */
  private resolveGroupIndex(groupElements: any[], loopIndex: number, displayedRealIndex?: number): number {
    const isSingleDisplayedElement = groupElements.length === 1
      && displayedRealIndex !== undefined
      && displayedRealIndex >= 0;
    return (isSingleDisplayedElement ? displayedRealIndex! : loopIndex);
  }

  /**
   * Vrai si l'élément n'a pas d'identifiant technique exploitable.
   */
  private isGroupIdMissing(groupElement: any): boolean {
    const idValue = groupElement?.id?.val;
    return idValue === null || idValue === undefined || idValue === '' || idValue === 'null';
  }

  /**
   * Lit l'évaluation du critère d'interdiction enregistré par FormInputComponent pour l'élément ciblé.
   * fallback : si l'évaluation de forbidden n'est pas connu → on régénère (comportement nominal, pas de risque d'écrasement backend).
   */
  private isRepeatableGroupForbidden(groupType: string, groupIndex: number): boolean {
    return this.eformConditionsV2Service.getRepeatableGroupForbiddenVerdict(groupType, groupIndex) === true;
  }

  /**
   * Affecte l'identifiant technique attendu (ex. 'CON-0001') à l'élément.
   */
  private assignRepeatableGroupId(element: any, groupType: string, groupIndex: number): void {
    if (!element.id) {
      element.id = {};
    }
    element.id.val = `${groupType}-${String(groupIndex + 1).padStart(4, '0')}`;
  }

  /**
   * Récupère le noticeResult depuis l'annonce
   */
  private getNoticeResult(annonce: Annonce): any {
    const ann = annonce?.ann as any;
    if (!ann) {
      return null;
    }

    // Chercher dans contractAwardNotice (F29)
    const notice = ann.contractAwardNotice as any;
    if (!notice) {
      return null;
    }

    // Essayer le format lowercase (venant du getRawValue() du formulaire Angular)
    const ublExt = notice.ublextensions?.ublextension?.[0] || notice.ublExtensions?.ublExtension?.[0];
    if (ublExt) {
      const extContent = ublExt.extensioncontent || ublExt.extensionContent;
      if (extContent) {
        const eformsExt = extContent.eformsextension || extContent.eformsExtension;
        if (eformsExt) {
          return eformsExt.noticeresult || eformsExt.noticeResult;
        }
      }
    }

    return null;
  }

  /**
   * ✅ FIX 2026-03-06: Régénère les IDs techniques des organisations (ORG-XXXX) dans le JSON
   * avant l'envoi au backend.
   *
   * Quand une nouvelle org est créée via "Ajouter nouveau", le FormGroup Angular est construit
   * avec des données vides (_isNewElement: true). Le champ company.partyidentification.id.val
   * est null. Sans cet ID, le backend ne sait pas quelle organisation sauvegarder et écrase
   * l'org 1 (ORG-0001) par défaut, causant la perte des données de l'org 1.
   *
   * Cette méthode utilise displayedRealIndex pour calculer le bon ID technique (ORG-0003 pour index 2, etc.)
   */
  private regenerateNullOrganizationIds(annonce: Annonce): void {
    const organizations = this.getOrganizations(annonce);
    if (!organizations || !Array.isArray(organizations) || organizations.length === 0) {
      return;
    }

    const displayedRealIndex = this.eformConditionsV2Service.getDisplayedRealIndex('ORG');

    for (let i = 0; i < organizations.length; i++) {
      const org = organizations[i];
      if (!org) {
        continue;
      }

      const partyId = org?.company?.partyidentification?.id?.val
        ?? org?.company?.partyIdentification?.id?.val;


      // Calculer l'ID attendu
      let expectedIdNumber: number;
      if (organizations.length === 1 && displayedRealIndex !== undefined && displayedRealIndex >= 0) {
        expectedIdNumber = displayedRealIndex + 1;
      } else {
        expectedIdNumber = i + 1;
      }
      const expectedId = 'ORG-' + String(expectedIdNumber).padStart(4, '0');

      // ✅ Régénérer si :
      // 1. L'ID est null/vide (nouvelle org sans ID)
      // 2. L'ID est ORG-0001 (résidu de l'org précédente dans le FormGroup) mais displayedRealIndex > 0
      //    → Ex: on sauvegarde l'org 3 (index=2) mais le FormGroup a encore ORG-0001
      //    ⚠️ Ne PAS corriger si le FormGroup a déjà un ID valide différent de ORG-0001
      //    (ex: ORG-0002 avec displayedRealIndex=0 = l'index est en retard, pas l'ID)
      const isNull = partyId === null || partyId === undefined || partyId === '' || partyId === 'null';
      const isResidualId = partyId === 'ORG-0001'
        && displayedRealIndex !== undefined
        && displayedRealIndex > 0;

      if (isNull || isResidualId) {
        if (org.company) {
          if (!org.company.partyidentification) {
            org.company.partyidentification = {};
          }
          if (!org.company.partyidentification.id) {
            org.company.partyidentification.id = {};
          }
          org.company.partyidentification.id.val = expectedId;
        }
      }
    }
  }

  /**
   * ✅ FIX 2026-04-03: Corrige l'ID du lot courant dans l'annonce extraite.
   *
   * Problème : au démarrage, getDuplicableEntity(LOT, 1) retourne les données du dernier lot
   * sauvegardé (ex: LOT-0006). Cet ID s'inscrit dans procurementprojectlot[0].id et reste figé
   * quelle que soit la navigation entre lots (seul le BT-131 et autres champs se mettent à jour).
   * Résultat : lors de la sauvegarde de lot3, le backend reçoit id=LOT-0006 et route le save
   * sur LOT-0006 → les modifications de lot3, lot4, etc. ne sont jamais persistées.
   *
   * Ce fix récupère le vrai ID du lot courant depuis listDuplicableEntity et le réinjecte dans
   * procurementprojectlot[0].id avant l'envoi au backend.
   *
   * @param annonce L'annonce extraite à corriger.
   * @param stepValue Valeur 1-basée de l'étape de lot (ex: 3 pour le 3ème lot).
   *                  Quand fourni, utilisé directement comme index fiable (stepValue-1).
   *                  Sinon, fallback sur actuallyIndexLotOrPar (peut être incorrect au 1er chargement).
   */
  public fixCurrentLotId(annonce: Annonce, stepValue?: number): void {
    if (!this.eformDuplicableEntityService) {
      return;
    }

    let lotIndex: number;
    if (stepValue !== null && stepValue !== undefined) {
      // ✅ stepValue est 1-basé (lot1=1, lot3=3) → index 0-basé dans listDuplicableEntity
      lotIndex = stepValue - 1;
    } else {
      lotIndex = this.eformDuplicableEntityService.actuallyIndexLotOrPar;
      if (lotIndex === null || lotIndex === undefined || lotIndex < 0) {
        return;
      }
    }
    const allEntities = this.eformDuplicableEntityService.listDuplicableEntity.getValue();
    if (!allEntities || allEntities.length === 0) {
      return;
    }
    const lotEntities = allEntities.filter(e => e?.id?.startsWith('LOT') || e?.id?.startsWith('PAR'));
    const correctLotId = lotEntities[lotIndex]?.id;
    if (!correctLotId) {
      return;
    }

    const ann = annonce?.ann as any;
    if (!ann) {
      return;
    }

    const noticeTypes = ['contractNotice', 'contractAwardNotice', 'priorInformationNotice', 'businessRegistrationInformationNotice'];
    for (const noticeType of noticeTypes) {
      const notice = ann[noticeType];
      if (!notice) {
        continue;
      }

      const lots = notice.procurementprojectlot;
      if (!lots || !Array.isArray(lots) || lots.length === 0) {
        continue;
      }

      const lot = lots[0];
      if (!lot) {
        break;
      }

      // Cas 1 : id est un objet {val: "LOT-XXXX"} (format form controls eForms)
      if (lot.id && typeof lot.id === 'object' && lot.id.val !== undefined) {
        if (lot.id.val !== correctLotId) {
          lot.id.val = correctLotId;
        }
      } else if (lot.id && typeof lot.id === 'string') {
        // Cas 2 : id est directement une string "LOT-XXXX"
        if (lot.id !== correctLotId) {
          lot.id = correctLotId;
        }
      }
      break; // Notice trouvée, on sort
    }
  }

  /**
   * ✅ FIX 2026-04-22: Tronque le tableau procurementprojectlot à un seul élément.
   *
   * Problème : après duplication d'un avis ENVOYÉ, le JSON de l'avis peut contenir tous les lots
   * (injectés par fillAvisWithDuplicableObjects côté backend lors de l'envoi BOAMP).
   * Le formulaire Angular ne gère qu'un seul lot à la fois. Si les N lots arrivent dans l'extraction,
   * le backend persist() met à jour les N lots avec des données potentiellement périmées
   * (seul le lot affiché a des données à jour). Sur Firefox, le timing HTTP cause un décalage
   * qui fait que le lot 2 est écrasé par le lot 1.
   *
   * Solution : ne garder que lots[0] (le lot courant affiché dans le stepper).
   */
  private truncateProcurementProjectLot(annonce: Annonce): void {
    const ann = annonce?.ann as any;
    if (!ann) {
      return;
    }

    const noticeTypes = ['contractNotice', 'contractAwardNotice', 'priorInformationNotice', 'businessRegistrationInformationNotice'];
    for (const noticeType of noticeTypes) {
      const notice = ann[noticeType];
      if (!notice) {
        continue;
      }

      const lots = notice.procurementprojectlot || notice.procurementProjectLot;
      if (Array.isArray(lots) && lots.length > 1) {
        lots.splice(1);
      }
      break; // Une seule notice active par avis
    }
  }

  /**
   * ✅ FIX 2026-03-06: Récupère le tableau organizations depuis l'annonce,
   * quel que soit le type de formulaire (contractNotice, contractAwardNotice, priorInformationNotice, etc.)
   */
  private getOrganizations(annonce: Annonce): any[] | null {
    const ann = annonce?.ann as any;
    if (!ann) {
      return null;
    }


    const noticeTypes = ['contractNotice', 'contractAwardNotice', 'priorInformationNotice', 'businessRegistrationInformationNotice'];

    for (const noticeType of noticeTypes) {
      const notice = ann[noticeType];
      if (!notice) {
        continue;
      }

      const ublExt = notice.ublextensions?.ublextension?.[0] || notice.ublExtensions?.ublExtension?.[0];
      if (!ublExt) {
        continue;
      }

      const extContent = ublExt.extensioncontent || ublExt.extensionContent;
      if (!extContent) {
        continue;
      }

      const eformsExt = extContent.eformsextension || extContent.eformsExtension;
      if (!eformsExt) {
        continue;
      }

      const orgs = eformsExt.organizations || eformsExt.Organizations;
      if (!orgs) {
        continue;
      }

      const orgArray = orgs.organization || orgs.Organization;
      if (Array.isArray(orgArray) && orgArray.length > 0) {
        return orgArray;
      }
    }

    return null;
  }


  /**
   * Mettre l'annonceur ici permet son ajout en BDD
   * @param form
   * @private
   */
  private extractAnnonceur(form): Annonceur {
    var annonceur = new Annonceur();
    annonceur.login = form.controls['_SepSep_idAnnonceur_SepAtt_idannonceurboampweb']?.value;
    annonceur.mail = form.controls['_SepSep_idAnnonceur_SepAtt_mailcontactboampweb']?.value;
    return annonceur;
  }

  setAvisCritere(formCode: any, avis: Avis) {

    switch (formCode) {
      case 'E_FORM_1':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_2':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_3':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_4':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_5':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_6':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_7':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_8':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_9':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_1;
        break;
      case 'E_FORM_10':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_11':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_12':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_13':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_14':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_15':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_16':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_17':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_18':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_19':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_20':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_21':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_22':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_23':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_24':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_2;
        break;
      case 'E_FORM_25':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_26':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_27':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_28':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_29':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_30':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_31':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_32':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_33':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_34':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_35':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_36':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_37':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_3;
        break;
      case 'E_FORM_38':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_4;
        break;
      case 'E_FORM_39':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_4;
        break;
      case 'E_FORM_40':
        avis.critere.typeAvis.code = TypeAvisEnum.C4_4;
        break;
      default:
        break;
    }
    return avis;
  }

  getFacturation(form): TypeFacturation {
    const typeFacturation: TypeFacturation = new TypeFacturation();
    if (form.controls._SepSep_TypeFacturation_SepAtt_noServiceExecutant?.value) {
      typeFacturation.noServiceExecutant = form.controls._SepSep_TypeFacturation_SepAtt_noServiceExecutant.value;
    }
    typeFacturation.libelle = new TypeAdresseTEMPOST();
    if (form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr1?.value) {
      typeFacturation.libelle.adr1 = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr1.value;
    }
    if (form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr2?.value) {
      typeFacturation.libelle.adr2 = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr2.value;
    }
    if (form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr3) {
      typeFacturation.libelle.adr3 = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr3.value;
    }
    if (form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr4?.value) {
      typeFacturation.libelle.adr4 = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_adr4.value;
    }
    typeFacturation.libelle.classeProfit = new ClasseProfit();
    typeFacturation.libelle.classeProfit.autresOrganismesPublics = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.autresOrganismesPublics ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.commune = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.commune ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.debiteurEtranger = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.debiteurEtranger ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.debiteurPrivePersMorale = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.debiteurPrivePersMorale ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.debiteurPrivePersPhysique = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.debiteurPrivePersPhysique ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.departement = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.departement ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.etablissementsPublicsSante = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.etablissementsPublicsSante ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.etat = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.etat ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.groupementCollectivites = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.groupementCollectivites ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.classeProfit.region = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region?.value === RadioCheckBoxEnum.region ? RadioCheckBoxEnum.emptyValue : null;
    typeFacturation.libelle.cp = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_cp?.value;
    typeFacturation.libelle.denomination = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_denomination?.value;
    typeFacturation.libelle.pays = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_pays?.value;
    if (typeFacturation.libelle.pays == '') {
      typeFacturation.libelle.pays = null;
    }
    typeFacturation.libelle.siret = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_siret?.value;
    if (form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_tvaIntracommunautaire?.value) {
      typeFacturation.libelle.tvaIntracommunautaire = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_tvaIntracommunautaire.value;
    }
    typeFacturation.libelle.ville = form.controls._SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepAtt_ville?.value;
    if (form.controls._SepSep_TypeFacturation_SepAtt_numBonCommande?.value) {
      typeFacturation.numBonCommande = form.controls._SepSep_TypeFacturation_SepAtt_numBonCommande.value.trim();
    }
    typeFacturation.typeEntite = form.controls._SepSep_TypeFacturation_SepAtt_typeEntite?.value;
    if (typeFacturation.typeEntite === 'PriveeAssujettie') {
      // Vidage du N° de service exécutant, masqué en IHM quand le type d'entité est PriveeAssujettie.
      typeFacturation.noServiceExecutant = null;
      typeFacturation.identifiantAdressage = form.controls._SepSep_TypeFacturation_SepAtt_identifiantAdressage?.value;
    } else {
      // Vidage de l'identifiant d'adressage, masqué en IHM quand le type d'entité n'est pas PriveeAssujettie.
      typeFacturation.identifiantAdressage = null;
    }
    return typeFacturation;
  }

  getIndexation(form): TypeIndexation {
    const indexation = new TypeIndexation();

    indexation.criteresEnvironnementaux = form.controls._SepSep_TypeIndexation_SepAtt_criteresEnvironnementaux?.value;
    indexation.criteresSociaux = form.controls._SepSep_TypeIndexation_SepAtt_criteresSociaux?.value;
    indexation.nomOrganisme = form.controls._SepSep_TypeIndexation_SepAtt_nomOrganisme?.value;
    indexation.objetAvis = form.controls._SepSep_TypeIndexation_SepAtt_objetAvis?.value;
    indexation.dateFinDiffusion = form.controls._SepSep_TypeIndexation_SepAtt_dateFinDiffusion?.value;
    indexation.motsClef = new TypeMotsClef();
    indexation.motsClef.motClef = form.controls._SepSep_TypeIndexation_SepSep_TypeMotsClef_SepAtt_motClef?.value;

    // Les deux critères sont obligatoires : à défaut de valeur saisie ou héritée, on les fixe à 'Non'.
    CriteresIndexationHelper.applyDefaults(indexation, form);

    // Filtrer les valeurs vides du tableau motClef pour éviter les erreurs de validation
    if (Array.isArray(indexation.motsClef.motClef)) {
      indexation.motsClef.motClef = indexation.motsClef.motClef.filter((val: any) => val && typeof val === 'string' && val.trim() !== '');
    }
    indexation.deptPublication = new TypeDept();
    if (form.controls._SepSep_TypeIndexation_SepSep_TypeDept_SepAtt_rappel) {
      // Filtrer les valeurs vides du tableau rappel pour éviter les erreurs de validation
      // et normaliser les valeurs mixtes (objets {code, libelle} ou strings) en codes strings purs
      const rappelValue = form.controls._SepSep_TypeIndexation_SepSep_TypeDept_SepAtt_rappel.value;
      if (Array.isArray(rappelValue)) {
        indexation.deptPublication.rappel = rappelValue
          .map(val => (val && typeof val === 'object' && val.code) ? val.code : val)
          .filter(val => val && typeof val === 'string' && val.trim() !== '');
      } else {
        indexation.deptPublication.rappel = rappelValue;
      }
    }
    indexation.deptPublication.deptPub = form.controls._SepSep_TypeIndexation_SepSep_TypeDept_SepAtt_deptPub?.value;

    return indexation;
  }


  /**
   * Nettoie les champs interdits dans les éléments duplicables de l'annonce.
   *
   * Cette méthode parcourt les éléments duplicables qui contiennent des champs interdits
   * et qui n'ont pas été chargés dans le FormGroup (accordéons fermés), puis met à NULL
   * ces champs directement dans l'annonce existante pour garantir qu'ils ne sont pas
   * envoyés au backend.
   */
  private injectCleanedDuplicableElements(annonce: Annonce): void {
    const elementsToClean = this.eformConditionsV2Service.getCleanedDuplicableElements();

    if (elementsToClean.length === 0) {
      return; // Rien à nettoyer
    }

    elementsToClean.forEach(element => {
      try {
        // Mapper le type vers la propriété de l'annonce
        const propertyPath = this.getDuplicablePropertyPath(element.type, annonce);

        if (propertyPath) {
          // Nettoyer les champs interdits dans l'élément existant
          this.cleanFieldsInElement(annonce, propertyPath, element.index, element.fieldsToClean);
        }
      } catch (error) {
        console.error(`[❌ ANNONCE] Erreur lors du nettoyage de ${element.type}_${element.index}:`, error);
      }
    });
  }

  /**
   * Retourne le chemin de la propriété dans l'annonce pour un type d'élément duplicable donné
   */
  private getDuplicablePropertyPath(type: string, annonce: Annonce): string {
    const pathMap: Record<string, string> = {
      'ORG': 'ann.priorInformationNotice.notice.organization', // ou contractNotice, contractAwardNotice selon le formulaire
      'RES': 'ann.contractAwardNotice.noticeResult.lotResult',
      'CON': 'ann.contractAwardNotice.noticeResult.settledContract',
      'TEN': 'ann.contractAwardNotice.lotTender',
      'TPA': 'ann.contractAwardNotice.tenderingParty'
    };

    // Déterminer le bon chemin selon le type de notice présent dans l'annonce
    if (type === 'ORG') {
      if (this.hasProperty(annonce, 'ann.priorInformationNotice')) {
        return 'ann.priorInformationNotice.notice.organization';
      } else if (this.hasProperty(annonce, 'ann.contractNotice')) {
        return 'ann.contractNotice.notice.organization';
      } else if (this.hasProperty(annonce, 'ann.contractAwardNotice')) {
        return 'ann.contractAwardNotice.notice.organization';
      }
    }

    return pathMap[type] || null;
  }

  /**
   * Vérifie si une propriété existe dans un objet en suivant un chemin
   */
  private hasProperty(obj: any, path: string): boolean {
    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return false;
      }
    }

    return current != null;
  }

  /**
   * Nettoie les champs interdits dans un élément duplicable de l'annonce.
   *
   * Stratégie selon les règles backend :
   * - null → Le backend SUPPRIME le champ
   * - "" (chaîne vide) → À utiliser UNIQUEMENT si le champ est mandatory (obligatoire XSD)
   *
   * La décision null vs "" se base sur la propriété mandatory du champ dans les métadonnées.
   */
  private cleanFieldsInElement(obj: any, path: string, index: number, fieldsToClean: Map<string, { mandatory: boolean }>): void {
    const parts = path.split('.');
    let current = obj;

    // Naviguer jusqu'à l'avant-dernier niveau
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part]) {
        return; // L'élément n'existe pas, rien à nettoyer
      }
      current = current[part];
    }

    // Dernière propriété : c'est un tableau
    const lastPart = parts[parts.length - 1];
    if (!current[lastPart] || !Array.isArray(current[lastPart])) {
      return; // Le tableau n'existe pas
    }

    const element = current[lastPart][index];
    if (!element) {
      return; // L'élément à l'index spécifié n'existe pas
    }

    // Nettoyer chaque champ selon sa propriété mandatory
    fieldsToClean.forEach((fieldInfo, fieldId) => {
      const cleanValue = fieldInfo.mandatory ? '' : null;
      this.setFieldValueRecursive(element, fieldId, cleanValue, fieldInfo.mandatory);
    });
  }

  /**
   * Met à null ou "" (chaîne vide) un champ dans un objet de manière récursive.
   *
   * Logique selon les règles backend :
   * - null → Si le champ n'est PAS mandatory → Le backend supprime le champ
   * - "" → Si le champ est mandatory → Satisfait le schéma XSD sans valeur réelle
   *
   * Cherche le champ par son ID (ex: "OPT-080-LotResult") dans toute la structure
   */
  private setFieldValueRecursive(obj: any, fieldId: string, cleanValue: string | null, isMandatory: boolean): boolean {
    if (!obj || typeof obj !== 'object') {
      return false;
    }

    // Parcourir toutes les propriétés de l'objet
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        // Si c'est un objet avec un inputId ou id qui correspond
        if (value && typeof value === 'object' && (value.inputId === fieldId || value.id === fieldId)) {
          // Mettre la valeur selon mandatory
          if ('val' in value) {
            obj[key].val = cleanValue;
          } else if ('value' in value) {
            obj[key].value = cleanValue;
          }
          return true; // Champ trouvé et nettoyé
        }

        // Recherche récursive
        if (typeof value === 'object') {
          if (this.setFieldValueRecursive(value, fieldId, cleanValue, isMandatory)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * ✅ FIX 2026-01-13: Nettoyage générique récursif des éléments vides dans toute l'annonce.
   *
   * Parcourt récursivement l'objet et pour chaque tableau trouvé :
   * - Supprime les éléments qui sont complètement vides (tous les champs null/vides)
   * - Préserve les tableaux structurels qui doivent toujours exister
   * - Nettoie récursivement les éléments non-vides pour supprimer leurs sous-éléments vides
   *
   * @param obj L'objet à nettoyer (généralement annonce.ann)
   * @param path Le chemin courant pour le logging (optionnel)
   */
  private cleanEmptyArrayElementsRecursive(obj: any, path: string = ''): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Tableaux structurels qui ne doivent JAMAIS être nettoyés
    // Ces tableaux font partie de la structure de base du formulaire
    const structuralArrays = [
      'organization',           // Organisations - toujours présentes
      'procurementprojectlot',  // Lots - toujours présents
      'ultimatebeneficialowner', // UBO dans organizations
      'partylegalentity',       // Entités légales dans organizations
      'partyname',              // Noms dans organizations
      'ublextension',           // Extensions UBL - structure de base
      'description',            // Descriptions multilingues
      'name',                   // Noms multilingues
      'note'                    // Notes multilingues
    ];

    // Parcourir toutes les propriétés de l'objet
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) {
        continue;
      }

      const value = obj[key];
      const currentPath = path ? `${path}.${key}` : key;

      if (Array.isArray(value)) {
        // Vérifier si c'est un tableau structurel
        const keyLower = key.toLowerCase();
        if (structuralArrays.includes(keyLower)) {
          // Pour les tableaux structurels, nettoyer récursivement les éléments mais ne pas les supprimer
          value.forEach((item, index) => {
            this.cleanEmptyArrayElementsRecursive(item, `${currentPath}[${index}]`);
          });
          continue;
        }

        // Pour les autres tableaux, filtrer les éléments vides
        const originalLength = value.length;
        const cleanedArray = value.filter((item, index) => {
          // D'abord nettoyer récursivement l'élément
          this.cleanEmptyArrayElementsRecursive(item, `${currentPath}[${index}]`);
          // Puis vérifier s'il est vide
          return !this.isEmptyRepeatableElement(item);
        });

        // Mettre à jour le tableau si des éléments ont été supprimés
        if (cleanedArray.length !== originalLength) {
          obj[key] = cleanedArray;
        }
      } else if (typeof value === 'object' && value !== null) {
        // Récursion pour les objets imbriqués
        this.cleanEmptyArrayElementsRecursive(value, currentPath);
      }
    }
  }

  /**
   * ✅ FIX 2026-01-08: Nettoie les éléments répétables vides/dupliqués dans les éléments duplicables majeurs.
   *
   * Quand on navigue entre les éléments duplicables (RES, ORG, etc.), le formulaire Angular peut créer
   * des éléments répétables vides (receivedSubmissionsStatistics, partyLegalEntity, etc.) à cause
   * du noticeValue global qui contient les données accumulées des sauvegardes précédentes.
   *
   * Cette méthode parcourt les éléments duplicables majeurs et supprime les éléments répétables
   * qui sont vides (tous les champs ont des valeurs null ou vides).
   *
   * ⚠️ IMPORTANT: Certains tableaux ne doivent PAS être nettoyés car ils font partie de la structure
   * normale du formulaire (ex: ultimatebeneficialowner, partylegalentity dans organization).
   */
  private cleanEmptyNestedRepeatableElements(annonce: Annonce): void {
    const ann = annonce?.ann as any;
    if (!ann) {
      return;
    }

    // Configuration des éléments duplicables majeurs et leurs tableaux imbriqués à nettoyer
    // ⚠️ NOTE: On exclut les tableaux structurels des organizations (partylegalentity, ultimatebeneficialowner, partyname)
    // car ils doivent toujours exister même s'ils sont vides
    const duplicableConfig: { [key: string]: { path: string[], nestedArrays: string[] }[] } = {
      'contractAwardNotice': [
        {
          path: ['ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lotresult'],
          nestedArrays: ['receivedsubmissionsstatistics', 'strategicprocurement']
        },
        {
          path: ['ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lottender'],
          nestedArrays: ['aggregatedamounts', 'subcontractingtermsdescription']
        },
        {
          path: ['ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'settledcontract'],
          nestedArrays: ['applicablelegalbasis']
        },
        {
          path: ['ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'tenderingparty'],
          nestedArrays: ['tenderer', 'subcontractor']
        }
        // ⚠️ On n'inclut PAS organization car ses tableaux imbriqués sont structurels
      ],
      'contractNotice': [
        // ⚠️ On n'inclut PAS organization car ses tableaux imbriqués sont structurels
      ],
      'priorInformationNotice': [
        // ⚠️ On n'inclut PAS organization car ses tableaux imbriqués sont structurels
      ]
    };

    // Parcourir chaque type de notice
    for (const [noticeType, configs] of Object.entries(duplicableConfig)) {
      const notice = ann[noticeType];
      if (!notice) {
        continue;
      }

      for (const config of configs) {
        // Naviguer jusqu'au tableau d'éléments duplicables majeurs
        let duplicableArray = notice;
        for (const pathPart of config.path) {
          if (duplicableArray && typeof duplicableArray === 'object') {
            duplicableArray = duplicableArray[pathPart];
          } else {
            duplicableArray = null;
            break;
          }
        }

        if (!Array.isArray(duplicableArray)) {
          continue;
        }

        // Pour chaque élément duplicable majeur
        for (const duplicableElement of duplicableArray) {
          if (!duplicableElement) {
            continue;
          }

          // Nettoyer chaque tableau imbriqué
          for (const nestedArrayName of config.nestedArrays) {
            const nestedArray = duplicableElement[nestedArrayName];
            if (!Array.isArray(nestedArray)) {
              continue;
            }

            // Filtrer les éléments vides
            const cleanedArray = nestedArray.filter(item => !this.isEmptyRepeatableElement(item));

            // Si on a supprimé des éléments, mettre à jour le tableau
            if (cleanedArray.length !== nestedArray.length) {
              duplicableElement[nestedArrayName] = cleanedArray;
            }
          }
        }
      }
    }
  }

  /**
   * Vérifie si un élément répétable est vide (tous les champs ont des valeurs null, undefined ou chaîne vide).
   */
  private isEmptyRepeatableElement(element: any): boolean {
    if (!element || typeof element !== 'object') {
      return true;
    }

    // Parcourir toutes les propriétés de l'élément
    for (const key in element) {
      if (!element.hasOwnProperty(key)) {
        continue;
      }

      const value = element[key];

      // Ignorer les propriétés techniques et structurelles
      // Ces propriétés existent toujours même quand l'élément est "vide"
      const ignoredKeys = [
        'inputid', 'inputId',
        'fieldsprivacy', 'fieldsPrivacy',  // Champ structurel toujours présent
        'listid', 'listId',
        'listagencyname', 'listAgencyName',
        'listschemeuri', 'listSchemeURI',
        'listuri', 'listURI',
        'name',
        'languageid', 'languageID',
        'listname', 'listName',
        'listagencyid', 'listAgencyID',
        'listversionid', 'listVersionID',
        'format'
      ];
      if (ignoredKeys.includes(key.toLowerCase())) {
        continue;
      }

      // Vérifier si la valeur est non-vide
      if (this.hasNonEmptyValue(value)) {
        return false; // L'élément a au moins une valeur non-vide
      }
    }

    return true; // Toutes les valeurs sont vides
  }

  /**
   * Vérifie si une valeur est non-vide de manière récursive.
   */
  private hasNonEmptyValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return true;
    }
    if (typeof value === 'boolean') {
      return true;
    }

    if (Array.isArray(value)) {
      // Un tableau est non-vide s'il contient au moins un élément non-vide
      return value.some(item => this.hasNonEmptyValue(item));
    }

    if (typeof value === 'object') {
      // Cas spécial pour les objets avec une propriété 'val'
      if ('val' in value) {
        const val = value.val;
        if (val !== null && val !== undefined && val !== '') {
          return true;
        }
      }

      // Vérifier récursivement les autres propriétés (sauf inputId, listName, etc.)
      for (const key in value) {
        if (!value.hasOwnProperty(key)) {
          continue;
        }
        if (key === 'inputid' || key === 'inputId' || key === 'listname' || key === 'listName') {
          continue;
        }
        if (this.hasNonEmptyValue(value[key])) {
          return true;
        }
      }
    }

    return false;
  }
}
